/**
 * Remembered audio choices for voice calls — output volume, and which microphone and speaker to
 * use.
 *
 * These persist because they are settings, not session state: someone who plugs in a headset and
 * picks it once should not have to pick it again on every call. Device ids are stable per origin
 * once permission has been granted, which is what makes remembering them work at all — and also
 * why a remembered id can be a dud on a different machine, handled at the point of use
 * (MicCapture.openStream retries without the constraint rather than refusing to start).
 */

const VOLUME_KEY = 'symphony-voice-volume';
const INPUT_KEY = 'symphony-voice-input-device';
const OUTPUT_KEY = 'symphony-voice-output-device';

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    // Private mode / storage disabled — defaults are fine.
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    /* not worth failing a call over */
  }
}

/** Output volume 0..1. Anything unparseable or out of range falls back to full. */
export function loadVolume(): number {
  const raw = read(VOLUME_KEY);
  const parsed = raw === null ? NaN : Number(raw);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 1 ? parsed : 1;
}

export function saveVolume(volume: number): void {
  write(VOLUME_KEY, String(volume));
}

/** `null` means "system default", which is a real choice and distinct from "never chose". */
export function loadInputDevice(): string | null {
  return read(INPUT_KEY);
}

export function saveInputDevice(deviceId: string | null): void {
  write(INPUT_KEY, deviceId);
}

export function loadOutputDevice(): string | null {
  return read(OUTPUT_KEY);
}

export function saveOutputDevice(deviceId: string | null): void {
  write(OUTPUT_KEY, deviceId);
}

/**
 * Chromium lists the OS default OUTPUT as a real device with this id, exactly as it does for the
 * input — and `setSinkId('')` (the unconstrained default) is NOT the same thing, the speaker twin
 * of the microphone bug that `preferredInputId` was written to cure. On macOS the empty sink stuck
 * on the built-in speaker while the OS default was a Jabra headset, so the agent's voice came out of
 * the laptop.
 */
export const SYSTEM_DEFAULT_OUTPUT_ID = 'default';

/**
 * Which sink id to route playback to — the output analogue of `preferredInputId`.
 *
 * A remembered id is used as-is (a deliberate choice is honoured). When nothing is remembered and
 * the browser publishes the real `deviceId === 'default'` audiooutput entry, we route to it
 * explicitly rather than to the unconstrained `''` sink — the entry tracks the OS default, which is
 * what "system default" means to the user. Firefox/Safari publish no such entry (or refuse to
 * enumerate), so we return null and the caller falls back to `''`, exactly as before.
 */
export async function preferredOutputId(remembered: string | null): Promise<string | null> {
  if (remembered) return remembered;
  try {
    const devices = await navigator?.mediaDevices?.enumerateDevices?.();
    const hasDefault = (devices ?? []).some(
      (d) => d.kind === 'audiooutput' && d.deviceId === SYSTEM_DEFAULT_OUTPUT_ID,
    );
    return hasDefault ? SYSTEM_DEFAULT_OUTPUT_ID : null;
  } catch {
    // enumerateDevices unavailable or refused — fall back to the unconstrained default sink.
    return null;
  }
}

/**
 * Route an `AudioContext`'s output to the preferred sink and swallow the failures that must never
 * take audio down. The single place both the voice call (`PcmPlayback`) and the spoken brief
 * (`PcmStreamPlayer`) resolve the speaker, so they always agree on where sound goes — the bug was
 * the brief playing on the system default while the call was on the chosen device.
 *
 * `setSinkId` is `AudioContext.setSinkId` (Chromium 110+); where it is absent this is a no-op and
 * playback stays on the default, which is why the picker is disabled rather than broken elsewhere.
 */
export async function applyPreferredSink(
  ctx: AudioContext | null | undefined,
  remembered: string | null,
): Promise<void> {
  // `AudioContext.setSinkId` is Chromium 110+ and not in the DOM lib types yet — read it through a
  // cast, and treat its absence as "this browser can't route output" (a no-op, default sink).
  const sink = ctx as unknown as { setSinkId?: (id: string) => Promise<void> } | null | undefined;
  if (!sink || typeof sink.setSinkId !== 'function') return;
  try {
    const preferred = await preferredOutputId(remembered);
    await sink.setSinkId(preferred ?? '');
  } catch (err) {
    // A device that vanished (unplugged headset) must not end the call/brief — the default keeps playing.
    console.warn('[voice] setSinkId failed', err);
  }
}

/**
 * Can this browser route audio to a chosen speaker?
 *
 * Probes `AudioContext.setSinkId` (Chromium 110+) rather than the HTMLMediaElement method,
 * because playback here is a Web Audio graph with no media element. Everywhere else the picker is
 * shown disabled with an explanation — a control that silently does nothing is worse than one
 * that admits it can't.
 */
export function outputDeviceSupported(): boolean {
  if (typeof AudioContext === 'undefined') return false;
  try {
    const probe = new AudioContext();
    const supported =
      typeof (probe as unknown as { setSinkId?: unknown }).setSinkId === 'function';
    void probe.close().catch(() => {});
    return supported;
  } catch {
    return false;
  }
}

export interface AudioDevice {
  deviceId: string;
  label: string;
}

/**
 * List input and output devices.
 *
 * Labels are blank until microphone permission has been granted on this origin — that is a
 * privacy rule, not a bug — so a caller that needs names must ask for the mic first. Devices with
 * no label still get a usable fallback name so the list is never a row of blanks.
 */
export async function listAudioDevices(): Promise<{ inputs: AudioDevice[]; outputs: AudioDevice[] }> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) {
    return { inputs: [], outputs: [] };
  }
  const devices = await navigator.mediaDevices.enumerateDevices();
  const pick = (kind: MediaDeviceKind, fallback: string): AudioDevice[] =>
    devices
      .filter((d) => d.kind === kind && d.deviceId)
      .map((d, i) => ({ deviceId: d.deviceId, label: d.label || `${fallback} ${i + 1}` }));
  return {
    inputs: pick('audioinput', 'Microphone'),
    outputs: pick('audiooutput', 'Speaker'),
  };
}

/**
 * Did enumerateDevices give us real names, or a row of blanks? Read from the RAW enumeration,
 * never from `listAudioDevices`, which synthesizes "Microphone 1" placeholders that would make
 * a blank list look populated.
 */
async function labelsPopulated(): Promise<boolean> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) return false;
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.some((d) => (d.kind === 'audioinput' || d.kind === 'audiooutput') && !!d.label);
  } catch {
    return false;
  }
}

/**
 * List devices with usable NAMES, asking for microphone permission only if it hasn't been
 * granted yet.
 *
 * `enumerateDevices` returns blank labels until some getUserMedia has succeeded on this origin
 * — a privacy rule — so a device picker opened before a call would otherwise be "Microphone 1,
 * Microphone 2", which is not a choice anyone can make. One throwaway getUserMedia populates
 * them, and the tracks are stopped immediately.
 *
 * The probe is skipped whenever labels are already populated. That is not an optimisation: on
 * macOS every getUserMedia flashes the orange privacy indicator, so re-probing on each open
 * would make opening a settings panel look like the app had started listening. (Same reasoning,
 * and the same shape, as wixel-fs's SettingsDialog.) It is also why this is called on OPEN and
 * never on mount — rendering a page must never cost a permission prompt.
 */
export async function listAudioDevicesWithLabels(): Promise<{
  inputs: AudioDevice[];
  outputs: AudioDevice[];
  permissionDenied: boolean;
}> {
  if (await labelsPopulated()) {
    return { ...(await listAudioDevices()), permissionDenied: false };
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    for (const track of stream.getTracks()) track.stop();
  } catch {
    return { ...(await listAudioDevices()), permissionDenied: true };
  }
  return { ...(await listAudioDevices()), permissionDenied: false };
}
